import * as THREE from 'three'
import { measureDirtShare, measureFarmState, type FarmState, type LawnSample, type TerrainSample, type WaterSample } from '../game/farm-state'
import { WATER_MIN_RENDER_DEPTH } from '../game/garden-water'
import { measureHarmony } from '../game/harmony'
import { largestHerd } from '../game/apex-raid'
import { containsFarmPoint } from '../game/farm-footprint'
import { ownsGardenTool, type createUpgradeLedger } from '../game/tool-unlocks'
import { GARDEN_TOOLS } from './garden-tool-art'
import type { GardenBounds } from '../game/farm-expansion'
import type { GardenTerrain } from './garden-terrain'
import type { createGardenWaterField } from '../game/garden-water'

export interface FarmMeasureDeps {
  readonly gardenSurface: () => THREE.Mesh | undefined
  readonly gardenTerrain: GardenTerrain | null
  readonly gardenWater: ReturnType<typeof createGardenWaterField> | null
  readonly gardenTools: { meadowArea(): number; shortGrassArea(): number } | null
  readonly gardenPlants: () => { readonly simulation: { readonly plants: readonly { readonly mature: boolean; readonly species: string }[] } } | null
  readonly gardenProps: () => { propCounts(): Record<string, number> } | null
  readonly residentCounts: () => Record<string, number>
  /** Adult residents (stage 3 up, not babies) and how full their bellies are, for Harmony. */
  readonly harmonyResidents: () => readonly { readonly species: string; readonly fullness: number }[]
  readonly preyEaten: () => Record<string, number>
  /** Visible pond cells that are frozen solid: they are not water to drink or paddle in. */
  readonly frozenWaterCells: () => number
  readonly activeGardenBounds: () => GardenBounds
  readonly upgrades: ReturnType<typeof createUpgradeLedger>
  /** Residents per species, babies and animals indoors included, for the biggest herd. */
  readonly herdCounts: () => Record<string, number>
  /** The newest open chapter's number, 1..6. */
  readonly chapterNumber: () => number
}

export function createFarmMeasure(deps: FarmMeasureDeps) {
  const { gardenSurface, gardenTerrain, gardenWater, gardenTools, gardenPlants, gardenProps, residentCounts, harmonyResidents, preyEaten, frozenWaterCells, activeGardenBounds, upgrades, herdCounts, chapterNumber } = deps
  // ------------------------------------------------------- farm measurement --

  /**
   * The lawn's own vertex grids, read straight out of the tool that maintains
   * them. This is the seam between the renderer and the simulation: the pure
   * condition code never sees a Three.js object, it only ever sees these arrays.
   */
  function currentLawnSample(): LawnSample | null {
    const lawn = gardenSurface()
    if (!lawn) return null
    const positions = lawn.geometry.getAttribute('position') as THREE.BufferAttribute
    const colors = lawn.geometry.getAttribute('color') as THREE.BufferAttribute | undefined
    if (!colors) return null
    const count = positions.count
    const xs = new Float32Array(count)
    const zs = new Float32Array(count)
    const coverage = new Float32Array(count)
    for (let index = 0; index < count; index += 1) {
      xs[index] = positions.getX(index)
      // The lawn geometry is authored in the XZ plane with +Y mapping to -Z.
      zs[index] = -positions.getY(index)
      coverage[index] = colors.getW(index)
    }
    return { count, xs, zs, coverage }
  }

  function currentTerrainSample(): TerrainSample | null {
    if (!gardenTerrain) return null
    // The height field's own grid; `cellSize`/`cols`/`rows` describe it exactly.
    const cols = gardenTerrain.gridCols
    const rows = gardenTerrain.gridRows
    const cellSize = gardenTerrain.cellSize
    const originX = gardenTerrain.originX
    const originZ = gardenTerrain.originZ
    const heights = new Float32Array(cols * rows)
    for (let gz = 0; gz < rows; gz += 1) {
      for (let gx = 0; gx < cols; gx += 1) {
        heights[gz * cols + gx] = gardenTerrain.heightAt(
          originX + gx * cellSize,
          originZ + gz * cellSize,
        )
      }
    }
    return { heights, cols, rows, cellSize, originX, originZ }
  }

  /** Debug: pin Harmony to a number, or null to measure it again. */
  let harmonyOverride: number | null = null

  /** The last measured farm, kept so the journal and harness can read it. */
  let lastFarmState: FarmState = { tallGrassArea: 0, waterArea: 0, flatGrassArea: 0, plantCounts: {}, residentCounts: {}, preyEaten: {}, propCounts: {} }

  function currentWaterSample(): WaterSample | null {
    if (!gardenWater) return null
    const summary = gardenWater.summary()
    return { visibleWetCells: Math.max(0, summary.visibleWetCells - frozenWaterCells()), cellSize: gardenWater.cellSize }
  }

  /**
   * Grown-up plants per species, which is what a `plantCount` condition reads.
   *
   * Only mature ones count, so an animal cannot be satisfied by seeds that have
   * been dropped in the water and forgotten. The plant simulation owns the truth
   * about growth; this only tallies it.
   */
  function maturePlantCounts(): Record<string, number> {
    const counts: Record<string, number> = {}
    for (const plant of gardenPlants()?.simulation.plants ?? []) {
      if (!plant.mature) continue
      counts[plant.species] = (counts[plant.species] ?? 0) + 1
    }
    return counts
  }

  /**
   * Tall meadow is measured from every grass blade, which is too much work to
   * redo each frame. Measure it at most once a second; anything that replaces
   * the grass outright (a load, a harness sow or clear) asks for a fresh one.
   */
  const MEADOW_REMEASURE_MS = 1000
  let meadowMeasuredAt = Number.NEGATIVE_INFINITY
  let meadowAreaCache = 0
  let shortGrassAreaCache = 0

  function currentMeadowArea(): number {
    const now = performance.now()
    if (now - meadowMeasuredAt >= MEADOW_REMEASURE_MS) {
      meadowAreaCache = gardenTools?.meadowArea() ?? 0
      shortGrassAreaCache = gardenTools?.shortGrassArea() ?? 0
      meadowMeasuredAt = now
    }
    return meadowAreaCache
  }

  function remeasureMeadow(): void {
    meadowMeasuredAt = Number.NEGATIVE_INFINITY
  }

  function measureFarm(): FarmState {
    const lawn = currentLawnSample()
    const terrain = currentTerrainSample()
    if (!lawn || !terrain) return lastFarmState
    lastFarmState = {
      ...measureFarmState(lawn, terrain, currentWaterSample(), maturePlantCounts()),
      meadowArea: currentMeadowArea(),
      shortGrassArea: shortGrassAreaCache,
      residentCounts: residentCounts(),
      harmony: harmonyOverride ?? measureHarmony(harmonyResidents()).score,
      terrainShares: {
        dirt: measureDirtShare(
          lawn,
          (x, z) => (gardenWater?.depthAt(x, z) ?? 0) >= WATER_MIN_RENDER_DEPTH,
          (x, z) => containsFarmPoint(x, z, activeGardenBounds()),
        ),
      },
      preyEaten: preyEaten(),
      propCounts: gardenProps()?.propCounts() ?? {},
      toolsOwned: Object.fromEntries(GARDEN_TOOLS.map((tool) => [tool.id, ownsGardenTool(upgrades, tool.id)])),
      chapter: chapterNumber(),
      largestHerd: largestHerd(herdCounts()),
    }
    lastMeasuredAt = performance.now()
    return lastFarmState
  }

  /**
   * The animal sim asks for the farm every frame, but measuring it scans every
   * lawn vertex and terrain cell, several milliseconds a frame for work that
   * only changes when the player digs, sows, waters or grows something. So the
   * sim takes a fresh read at most once a second, the same cadence as the
   * meadow. Explicit reads (sowing, the journal, the harness) always measure,
   * and they restart this clock.
   */
  const FARM_SIM_REMEASURE_MS = 1000
  let lastMeasuredAt = Number.NEGATIVE_INFINITY

  function measureFarmForSim(): FarmState {
    if (performance.now() - lastMeasuredAt < FARM_SIM_REMEASURE_MS) return lastFarmState
    return measureFarm()
  }

  return {
    setHarmonyOverride(value: number | null): void {
      harmonyOverride = value
      lastMeasuredAt = Number.NEGATIVE_INFINITY
    },
    measureFarm,
    measureFarmForSim,
    remeasureMeadow,
    maturePlantCounts,
  }
}
